/**
 * Administracao da plataforma: empresas, governanca, trilha, backups e janelas
 * de manutencao.
 *
 * O escopo acima das empresas. Ele nao depende de permissao de empresa: o servidor
 * confere a concessao propria a cada requisicao, e a tela so oferece o caminho a
 * quem a sessao diz que administra a plataforma.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams } from './comum';

import { api, requestBlob, requestPage } from '../client';

export type PlatformPlan = 'starter' | 'professional' | 'enterprise';
export type PlatformTenantStatus = 'active' | 'suspended' | 'closed';
export type PlatformFeatureStatus = 'active' | 'trial' | 'suspended' | 'expired';

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  plan: PlatformPlan;
  status: PlatformTenantStatus;
  created_at: string;
  member_count: number;
  active_feature_count: number;
}

export interface PlatformEntitlement {
  key: string;
  module: string;
  name: string;
  description: string;
  /** Nula quando a empresa nunca teve a funcionalidade. */
  status: PlatformFeatureStatus | null;
  expires_at: string | null;
  in_plan: boolean;
  usable: boolean;
}

export interface PlatformTenantDetail extends PlatformTenant {
  legal_name: string;
  document: string;
  updated_at: string;
  features: PlatformEntitlement[];
}

export interface PlatformTenantInput {
  slug: string;
  name: string;
  legal_name: string;
  document: string;
  plan: PlatformPlan;
  first_admin?: { email: string; name: string };
}

export interface PlatformInvitation {
  email: string;
  name: string;
  expires_at: string;
  accept_url: string;
  delivered_by_email: boolean;
}

export interface PlatformTenantCreated {
  tenant: PlatformTenantDetail;
  first_admin_invitation: PlatformInvitation | null;
}

export interface PlatformTenantUpdate {
  plan?: PlatformPlan;
  status?: 'active' | 'suspended';
  reason?: string;
}

export interface PlatformFeatureInput {
  status: PlatformFeatureStatus;
  expires_at?: string | null;
}

export interface PlatformFeatureOffer {
  key: string;
  module: string;
  name: string;
  description: string;
  plans: PlatformPlan[];
}

export interface PlatformAuditRecord {
  id: string;
  actor_user_id: string | null;
  actor_label: string;
  action: string;
  target_tenant_id: string | null;
  target_label: string;
  before: unknown;
  after: unknown;
  request_id: string;
  occurred_at: string;
}

/**
 * Permissoes que a administracao da plataforma governa em cada empresa. A lista e
 * fechada no servidor: chave fora dela e recusada.
 */
export type GovernedPermissionKey =
  'corretores.dados_bancarios.update' | 'tomadores.credito.update';

export interface PlatformGovernedRole {
  role_id: string;
  key: string;
  name: string;
  granted: boolean;
  /** Papel que a governanca nao muda, como o de acesso total. */
  locked: boolean;
}

export interface PlatformGovernance {
  tenant_id: string;
  issuance_requires_documents: boolean;
  permissions: { key: GovernedPermissionKey; roles: PlatformGovernedRole[] }[];
}

export interface PlatformGovernanceInput {
  issuance_requires_documents?: boolean;
  /** Para cada permissao informada, a lista de papeis que passa a te-la. */
  grants?: Partial<Record<GovernedPermissionKey, string[]>>;
}

export const platform = {
  listTenants: (params: ListParams, signal?: AbortSignal) =>
    requestPage<PlatformTenant>(`/api/v1/platform/tenants${query(params)}`, signal),
  getTenant: (tenantId: string, signal?: AbortSignal) =>
    api.get<PlatformTenantDetail>(`/api/v1/platform/tenants/${tenantId}`, signal),
  createTenant: (input: PlatformTenantInput) =>
    api.post<PlatformTenantCreated>('/api/v1/platform/tenants', input),
  updateTenant: (tenantId: string, input: PlatformTenantUpdate) =>
    api.patch<PlatformTenantDetail>(`/api/v1/platform/tenants/${tenantId}`, input),
  setFeature: (tenantId: string, featureKey: string, input: PlatformFeatureInput) =>
    api.put<PlatformTenantDetail>(
      `/api/v1/platform/tenants/${tenantId}/features/${encodeURIComponent(featureKey)}`,
      input,
    ),
  getGovernance: (tenantId: string, signal?: AbortSignal) =>
    api.get<PlatformGovernance>(
      `/api/v1/platform/tenants/${tenantId}/governance`,
      signal,
    ),
  updateGovernance: (tenantId: string, input: PlatformGovernanceInput) =>
    api.put<PlatformGovernance>(
      `/api/v1/platform/tenants/${tenantId}/governance`,
      input,
    ),
  features: (signal?: AbortSignal) =>
    api.get<PlatformFeatureOffer[]>('/api/v1/platform/features', signal),
  audit: (params: ListParams, signal?: AbortSignal) =>
    requestPage<PlatformAuditRecord>(`/api/v1/platform/audit${query(params)}`, signal),
};

// --- Backups da plataforma ---------------------------------------------------
//
// Os backups sao arquivos na pasta /backup do servidor. O agendamento do servidor
// grava os dele, e esta tela lista, cria, baixa e envia ao Google Drive. Criar e
// enviar respondem antes de terminar: a lista diz quando acabou.

export type BackupKind = 'banco' | 'arquivos';
export type BackupRequestKind = 'banco' | 'arquivos' | 'completo';
export type BackupOrigin = 'agendado' | 'manual';
export type BackupDriveState = 'nao_enviado' | 'enviando' | 'enviado' | 'falhou';
export type BackupTrigger = 'manual' | 'automatico';

export interface BackupDriveStatus {
  status: BackupDriveState;
  uploaded_at: string | null;
  attempted_at: string | null;
  url: string | null;
  error: string | null;
}

export interface BackupItem {
  name: string;
  kind: BackupKind;
  origin: BackupOrigin;
  size_bytes: number;
  created_at: string;
  drive: BackupDriveStatus;
}

export interface BackupJob {
  kind: BackupRequestKind;
  status: 'em_andamento' | 'concluido' | 'falhou';
  started_at: string;
  started_by: string;
  finished_at: string | null;
  files: { name: string; kind: BackupKind; size_bytes: number }[];
  error: string | null;
}

export interface BackupListing {
  items: BackupItem[];
  running: BackupJob | null;
  last_job: BackupJob | null;
  files_available: boolean;
}

export interface BackupUpload {
  name: string;
  trigger: BackupTrigger;
  started_at: string;
  started_by: string;
}

export interface BackupGoogleStatus {
  configured: boolean;
  missing: string[];
  redirect_url: string;
  connected: boolean;
  account_email: string | null;
  folder_id: string | null;
  folder_name: string | null;
  folder_url: string | null;
  auto_upload: boolean;
  connected_at: string | null;
  connected_by: string | null;
  last_upload: {
    name: string;
    status: 'enviado' | 'falhou';
    trigger: BackupTrigger;
    finished_at: string;
    url: string | null;
    error: string | null;
  } | null;
  uploading: BackupUpload | null;
}

export interface BackupGoogleUpdate {
  folder_name?: string;
  auto_upload?: boolean;
}

const caminhoDoBackup = (name: string) =>
  `/api/v1/platform/backups/${encodeURIComponent(name)}`;

export const platformBackups = {
  list: (signal?: AbortSignal) =>
    api.get<BackupListing>('/api/v1/platform/backups', signal),
  create: (kind: BackupRequestKind) =>
    api.post<BackupJob>('/api/v1/platform/backups', { kind }),
  download: (name: string) => requestBlob(`${caminhoDoBackup(name)}/download`),
  sendToDrive: (name: string) =>
    api.post<BackupUpload>(`${caminhoDoBackup(name)}/drive`),
  google: (signal?: AbortSignal) =>
    api.get<BackupGoogleStatus>('/api/v1/platform/backups/google', signal),
  connectGoogle: () =>
    api.post<{ authorization_url: string; expires_at: string }>(
      '/api/v1/platform/backups/google/connect',
    ),
  updateGoogle: (input: BackupGoogleUpdate) =>
    api.patch<BackupGoogleStatus>('/api/v1/platform/backups/google', input),
  disconnectGoogle: () =>
    api.delete<{ account_email: string; revoked_at_google: boolean }>(
      '/api/v1/platform/backups/google',
    ),
};

// --- Janelas de manutencao (P-ACE-1) ---------------------------------------

export type MaintenanceWindowStatus =
  'agendada' | 'em_andamento' | 'encerrada' | 'cancelada';

export interface MaintenanceWindow {
  id: string;
  status: MaintenanceWindowStatus;
  starts_at: string;
  ends_at: string | null;
  message: string;
  created_by: string;
  created_at: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  updated_at: string;
}

export interface MaintenanceWindowInput {
  starts_at?: string;
  ends_at?: string;
  message: string;
}

export interface MaintenanceWindowUpdate {
  starts_at?: string;
  /** Nulo deixa a janela sem previsao de retorno. */
  ends_at?: string | null;
  message?: string;
  action?: 'encerrar' | 'cancelar';
}

export const platformMaintenance = {
  list: (signal?: AbortSignal) =>
    api.get<MaintenanceWindow[]>('/api/v1/platform/maintenance-windows', signal),
  create: (input: MaintenanceWindowInput) =>
    api.post<MaintenanceWindow>('/api/v1/platform/maintenance-windows', input),
  update: (windowId: string, input: MaintenanceWindowUpdate) =>
    api.patch<MaintenanceWindow>(
      `/api/v1/platform/maintenance-windows/${encodeURIComponent(windowId)}`,
      input,
    ),
};

// --- Configuracao de plataforma da empresa (P-PLAT) ----------------------------
//
// Os campos de plataforma da empresa do OnPoint (LimitClaimsPerMonth,
// AllowExceedClaimsLimit, LimitDiskSpaceGB, AllowExceedDiskSpaceLimit,
// HasExternalClaimUploadAPI, IsNew e JSONPortalEnvironment). So a administracao da
// plataforma grava; a empresa le a propria pela rota da empresa corrente. So a cota de
// disco tem efeito: o envio de documento confere o espaco usado.

export interface PlatformTenantSettings {
  tenant_id: string;
  /** Nulo e sem limite registrado. Sem efeito. */
  limit_claims_per_month: number | null;
  allow_exceed_claims_limit: boolean;
  /** Cota de disco em gigabytes de 1024^3 bytes. Nula e sem cota. */
  limit_disk_space_gb: number | null;
  allow_exceed_disk_space_limit: boolean;
  has_external_claim_upload_api: boolean;
  is_new: boolean;
  /** Objeto JSON do portal. Nulo e sem configuracao. */
  portal_environment: Record<string, unknown> | null;
  /**
   * O mesmo JSON, no texto exato que foi gravado. A tela edita e envia este texto,
   * para que salvar outro campo nao regrave o JSON diferente (R5-M23). Ausente nas
   * respostas antigas.
   */
  portal_environment_text?: string | null;
  disk_used_bytes: number;
  disk_limit_bytes: number | null;
  disk_over_limit: boolean;
  updated_label: string;
  /** Nulo enquanto a empresa nunca foi configurada. */
  updated_at: string | null;
}

/** O PUT substitui a configuracao inteira: todo campo e obrigatorio. */
export interface PlatformTenantSettingsInput {
  limit_claims_per_month: number | null;
  allow_exceed_claims_limit: boolean;
  limit_disk_space_gb: number | null;
  allow_exceed_disk_space_limit: boolean;
  has_external_claim_upload_api: boolean;
  is_new: boolean;
  /** Objeto JSON, ou o texto com o objeto dentro, como o OnPoint entrega. */
  portal_environment: Record<string, unknown> | string | null;
}

export const platformSettings = {
  get: (tenantId: string, signal?: AbortSignal) =>
    api.get<PlatformTenantSettings>(
      `/api/v1/platform/tenants/${tenantId}/platform-settings`,
      signal,
    ),
  update: (tenantId: string, input: PlatformTenantSettingsInput) =>
    api.put<PlatformTenantSettings>(
      `/api/v1/platform/tenants/${tenantId}/platform-settings`,
      input,
    ),
  /** A empresa da sessao le a propria configuracao (configuracoes.tenant.view). */
  current: (signal?: AbortSignal) =>
    api.get<PlatformTenantSettings>(
      '/api/v1/tenants/current/platform-settings',
      signal,
    ),
};

// --- E-mail da plataforma (ADR 0096) --------------------------------------------
//
// A configuracao do envio de e-mail da plataforma, em /admin > Configuracoes > E-mail.
// A senha da conta e so escrita: nunca volta, a resposta diz so se ha senha definida.

export type PlatformEmailSecurity = 'ssl_implicito' | 'starttls';

export interface PlatformEmailSettings {
  enabled: boolean;
  host: string;
  port: number;
  security: PlatformEmailSecurity;
  username: string;
  /** Se ha senha gravada. A senha nunca volta. */
  password_set: boolean;
  from_address: string;
  from_name: string;
  reply_to: string;
  timeout_seconds: number;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  last_test_error: string;
  /** Ultimo teste bem-sucedido. */
  last_success_at: string | null;
  updated_at: string | null;
  updated_by: string;
  /** Se os e-mails saem agora: ligado, completo e com a senha legivel. */
  active: boolean;
  /** Se o servidor tem a chave que cifra a senha (SEGURACORE_SECRETS_KEY). */
  secrets_key_configured: boolean;
  /** Por que a configuracao ligada nao esta ativa. */
  problem: string;
}

export interface PlatformEmailSettingsInput {
  enabled: boolean;
  host: string;
  port: number;
  security: PlatformEmailSecurity;
  username: string;
  /** Vazia mantem a senha gravada. */
  password?: string;
  from_address: string;
  from_name: string;
  reply_to: string;
  timeout_seconds: number;
}

export interface PlatformEmailTestResult {
  ok: boolean;
  to: string;
  /** Categoria da falha: conexao, prazo, certificado, autenticacao... Vazia no sucesso. */
  category: string;
  message: string;
  at: string;
}

export type PlatformEmailKind =
  | 'redefinicao_senha'
  | 'convite'
  | 'autocadastro'
  | 'ativacao'
  | 'boas_vindas'
  | 'teste';

export interface PlatformEmailDelivery {
  id: string;
  kind: PlatformEmailKind;
  tenant_id: string | null;
  recipient_masked: string;
  status: 'enviado' | 'falhou';
  failure_category: string;
  failure_message: string;
  created_at: string;
}

export const platformEmail = {
  get: (signal?: AbortSignal) =>
    api.get<PlatformEmailSettings>('/api/v1/platform/email-settings', signal),
  save: (input: PlatformEmailSettingsInput) =>
    api.put<PlatformEmailSettings>('/api/v1/platform/email-settings', input),
  test: (to: string) =>
    api.post<PlatformEmailTestResult>('/api/v1/platform/email-settings/test', { to }),
  deliveries: (signal?: AbortSignal) =>
    api.get<PlatformEmailDelivery[]>('/api/v1/platform/email-deliveries', signal),
};
